import api, { apiErrorHandler } from "api";
import { FleetReportsConstants } from "constants/constants";
import { FleetReportsTableColumn } from "pages/FleetReports/tableData";
import { RootState } from "reducers/rootReducer";
import { Dispatch } from "redux";
import { FleetReportData } from "types/FleetReports";

export const getFleetReportsData =
  (params: { month?: number; year?: number }) => (dispatch: Dispatch) => {
    const onError = () => dispatch({ type: FleetReportsConstants.FLEET_REPORTS_ERROR });
    dispatch({ type: FleetReportsConstants.FLEET_REPORTS_LOADING });
    api
      .getFleetReportsData(params)
      .then((res) => {
        if (res?.data) {
          dispatch({
            type: FleetReportsConstants.FLEET_REPORTS_GET_DATA_SUCCESS,
            payload: {
              dataList: res?.data.dataList,
              offBoardedDataList: res?.data.offBoardedDataList,
            },
          });
        }
      })
      .catch(apiErrorHandler(onError));
  };

export const fleetReportsSetFilter =
  (data: any) => (dispatch: Dispatch, getStore: () => RootState) => {
    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_FILTERS,
      payload: { ...data },
    });
  };

export const createUpdateFleetReport =
  (name: string, value: number | string, vanNumber: string, vanMonth: number, vanYear: number) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const patchList = (list: FleetReportData[]) =>
      list.map((item) =>
        item.van_number === vanNumber && item.month === vanMonth && item.year === vanYear
          ? { ...item, [name]: value }
          : item,
      );
    const { fleetReportList, offBoardedFleetReportList } = getStore().fleetReports;
    dispatch({
      type: FleetReportsConstants.UPDATE_FLEET_REPORT_SUCCESS,
      payload: {
        list: patchList(fleetReportList),
        offBoardedList: patchList(offBoardedFleetReportList),
      },
    });
    api
      .createUpdateActualInvoice({
        name,
        value,
        vanNumber: vanNumber,
        month: vanMonth,
        year: vanYear,
      })
      .catch(apiErrorHandler());
  };

export const resetFleetReportsTableColumns =
  () => (dispatch: Dispatch, getStore: () => RootState) => {
    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_FILTERS,
      payload: {
        activeColumns: [
          "company",
          "model",
          "station",
          "size",
          "subtype",
          "van_number",
          "plate",
          "vin",
          "status",
          "onboarded_date",
          "offboarded_date",
          "rate",
          "invoice",
          "payroll_comment",
          "rental_period",
          "rental_period_to",
          "invoice_date",
          "paid_amount",
          "payment_date",
          "invoice_number",
          "payment_method",
          "deductibles_amount",
          "deductibles_invoice_number",
          "deductibles_payment_date",
          "deductibles_paid_amount",
          "deductibles_payment_method",
        ],
      },
    });
  };

export const editFleetReportsTableColumns =
  (id: string) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { fleetReportFilters } = getStore()?.fleetReports;
    const newData = fleetReportFilters?.activeColumns?.includes(id)
      ? fleetReportFilters?.activeColumns?.filter((item) => item !== id)
      : [...fleetReportFilters?.activeColumns, id];
    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_FILTERS,
      payload: { activeColumns: newData },
    });
  };

export const fleetReportsSetStatusFilter = (statuses: number[]) => (dispatch: Dispatch) => {
  dispatch({
    type: FleetReportsConstants.FLEET_REPORTS_SET_STATUS_LIST_FILTER,
    payload: statuses,
  });
};

export const fleetReportsSelectAllVans =
  (rows: FleetReportData[]) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { selectedIdList } = getStore()?.fleetReports;
    const selectableRows = rows.filter((row) => row.rate);
    const allSelected =
      selectableRows.length > 0 &&
      selectableRows.every((row) =>
        selectedIdList.some(
          (item) =>
            item.van === row.van_number && item.month === row.month && item.year === row.year,
        ),
      );

    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_SELECTED_VANS,
      payload: allSelected
        ? []
        : selectableRows.map((row) => ({
            van: row.van_number,
            month: row.month,
            year: row.year,
          })),
    });
  };

export const fleetReportsSelectVans =
  (van: string, month: number, year: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { selectedIdList } = getStore()?.fleetReports;
    const dataList = selectedIdList ? selectedIdList : [];
    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_SELECTED_VANS,
      payload: !dataList?.some(
        (item) => item.van === van && item.month === month && item.year === year,
      )
        ? [...dataList, { van, month, year }]
        : [
            ...dataList?.filter((item) => {
              return !(item.van === van && item.month === month && item.year === year);
            }),
          ],
    });
  };

export const fleetReportsMultipleUpdateReports =
  (
    invoiceNumber: string,
    invoiceDate: string,
    invoiceAmount: number,
    paymentDate: string,
    paymentAmount: number,
    paymentMethod: number,
  ) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const { selectedIdList, fleetReportList, offBoardedFleetReportList } = getStore()?.fleetReports;
    const selectedKeys = new Set(
      selectedIdList.map((item) => `${item.van}-${item.month}-${item.year}`),
    );

    api
      .createUpdateMultipleFleetReports({
        idList: selectedIdList,
        invoiceNumber,
        invoiceDate,
        invoiceAmount,
        paymentDate,
        paymentAmount,
        paymentMethod,
      })
      .then((res) => {
        if (!res) return;
        const patch = {
          invoice_number: invoiceNumber,
          invoice_date: invoiceDate,
          invoice: Number(invoiceAmount),
          payment_date: paymentDate,
          paid_amount: Number(paymentAmount),
          payment_method: Number(paymentMethod),
        };
        const patchList = (list: FleetReportData[]) =>
          list.map((item) =>
            selectedKeys.has(`${item.van_number}-${item.month}-${item.year}`)
              ? { ...item, ...patch }
              : item,
          );
        dispatch({
          type: FleetReportsConstants.FLEET_REPORTS_UPDATE_MULTIPLE_REPORTS,
          payload: {
            dataList: patchList(fleetReportList),
            offBoardedDataList: patchList(offBoardedFleetReportList),
          },
        });
      })
      .catch(apiErrorHandler());
  };

export const fleetReportsSelectFixedCols =
  (col: FleetReportsTableColumn, index: number) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const { fleetReportFilters } = getStore()?.fleetReports;

    if (col.id === "checkbox") return;

    const newFixedColumns = fleetReportFilters?.fixedColumns
      ? fleetReportFilters?.fixedColumns?.some((item) => item === col.id)
        ? fleetReportFilters?.fixedColumns?.filter((item) => item !== col.id)
        : [...fleetReportFilters?.fixedColumns, col.id]
      : [col.id];

    dispatch({
      type: FleetReportsConstants.FLEET_REPORTS_SET_FILTERS,
      payload: { fixedColumns: newFixedColumns },
    });
  };
